<template>
  <div class="p-6">
    <div class="flex justify-between items-center mb-6">
      <h2 class="text-2xl font-semibold">My Web Pages</h2>
      <Button variant="outline" size="sm" :disabled="isSharingSet" @click="handleShareSet">
        <Icon v-if="!isSharingSet" name="heroicons:share" class="h-4 w-4 mr-1.5" />
        <Icon v-else name="heroicons:arrow-path" class="h-4 w-4 mr-1.5 animate-spin" />
        {{ isSharingSet ? 'Creating Link...' : 'Share Web Set' }}
      </Button>
    </div>

    <div class="mb-4">
      <FilterInput v-model="searchQuery" placeholder="Search web pages..." />
    </div>

    <div v-if="randomWebPage" class="mb-6">
      <h3 class="text-xl font-semibold mb-4 text-center">Random Web Page Suggestion</h3>
      <AppLink v-if="randomWebPage" :to="`/bubble/${randomWebPage.uuid}`">
        <Card class="max-w-md mx-auto cursor-pointer">
          <div class="flex">
            <div class="w-1/3">
              <AspectRatio :ratio="1">
                <div class="relative w-full h-full">
                  <NuxtImg
                    v-if="getCoverPhotoUrl(randomWebPage)"
                    :src="getCoverPhotoUrl(randomWebPage)"
                    :alt="randomWebPage.title"
                    class="rounded-l-lg object-cover w-full h-full"
                  />
                  <div v-else class="flex items-center justify-center w-full h-full bg-gray-500">
                    <Icon name="heroicons:globe-alt" class="text-white w-8 h-8" />
                  </div>
                </div>
              </AspectRatio>
            </div>
            <div class="w-2/3 flex flex-col justify-between p-4">
              <div>
                <h4 class="font-semibold">{{ randomWebPage.title }}</h4>
                <p class="text-sm text-gray-500">{{ getDomain(randomWebPage.url) }}</p>
              </div>
              <Button class="mt-2" size="sm" @click.stop="visitPage(randomWebPage.url)"
                >Visit</Button
              >
            </div>
          </div>
        </Card>
      </AppLink>
    </div>
    <Separator class="my-4" />

    <div v-if="bubblesStore.loading" class="text-center py-8">
      <p class="text-gray-600">Loading web pages...</p>
    </div>
    <div v-else-if="bubblesStore.error" class="text-center py-8 text-red-500">
      <p>{{ bubblesStore.error }}</p>
    </div>
    <div v-else-if="filteredWebPages.length === 0" class="text-center py-8">
      <p class="text-gray-600 mb-4">You don't have any web pages yet.</p>
      <Button @click="navigateToAddWebPage">Add Your First Web Page</Button>
    </div>
    <div v-else class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      <AppLink
        v-for="webpage in filteredWebPages"
        :key="webpage.uuid"
        :to="`/bubble/${webpage.uuid}`"
      >
        <Card class="overflow-hidden cursor-pointer flex flex-col justify-between">
          <div>
            <CardContent class="p-0">
              <AspectRatio :ratio="16 / 9">
                <div class="relative w-full h-full">
                  <NuxtImg
                    v-if="getCoverPhotoUrl(webpage)"
                    :src="getCoverPhotoUrl(webpage)"
                    :alt="webpage.title"
                    class="object-cover w-full h-full transition-transform duration-300 hover:scale-110"
                  />
                  <div v-else class="flex items-center justify-center w-full h-full bg-gray-500">
                    <Icon name="heroicons:globe-alt" class="text-white w-12 h-12" />
                  </div>
                </div>
              </AspectRatio>
            </CardContent>
            <CardHeader>
              <CardTitle class="text-lg">{{ webpage.title }}</CardTitle>
              <CardDescription>{{ getDomain(webpage.url) }}</CardDescription>
            </CardHeader>
          </div>
          <CardFooter class="flex justify-center mt-auto">
            <Button variant="default" size="sm" class="w-full" @click.stop="visitPage(webpage.url)"
              >Visit</Button
            >
          </CardFooter>
        </Card>
      </AppLink>
    </div>

    <!-- Load More Button -->
    <div v-if="canLoadMore" class="mt-6 text-center">
      <Button :disabled="isLoading" variant="default" class="hover:opacity-90" @click="loadMore">
        Load More Web Pages
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, h } from 'vue'
import { useBubblesStore } from '~/stores/bubbles'
import { useUserShareStore } from '~/stores/userShare'
import type { WebPageBubble } from '~/stores/interfaces'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { AspectRatio } from '@/components/ui/aspect-ratio'
import { Button } from '@/components/ui/button'
import FilterInput from '~/components/bubbles/view/helpers/FilterInput.vue'
import { useRouter } from 'vue-router'
import { useRuntimeConfig } from '#app'
import { useToast } from '@/components/ui/toast'
import CopyButton from '@/components/ui/button/CopyButton.vue'
import { useNavigationStore } from '@/stores/navigationStore'
import AppLink from '@/components/ui/link/AppLink.vue'
// import { Badge } from '@/components/ui/badge'
// import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { useClipboard } from '@vueuse/core'

const router = useRouter()
const bubblesStore = useBubblesStore()
const navigationStore = useNavigationStore()
const userShareStore = useUserShareStore()
const { toast } = useToast()
const { copy, copied, isSupported } = useClipboard()
const searchQuery = ref('')
const randomWebPage = ref<WebPageBubble | null>(null)
const {
  public: { dataStore },
} = useRuntimeConfig()

const WEBPAGES_PER_PAGE = 12
const page = ref(1)
const isLoading = ref(false)
const error = ref<string | null>(null)
const isSharingSet = ref(false)

const webPageBubbles = computed(() => {
  return (bubblesStore.bubblesByType['web'] || []).map((bubble) => bubble as WebPageBubble)
})

const filteredWebPages = computed(() => {
  const filtered = [...webPageBubbles.value]
    .filter((webpage) => webpage.title.toLowerCase().includes(searchQuery.value.toLowerCase()))
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, page.value * WEBPAGES_PER_PAGE)

  return filtered
})

const canLoadMore = computed(() => {
  return webPageBubbles.value.length > page.value * WEBPAGES_PER_PAGE
})

onMounted(async () => {
  if (!bubblesStore.bubbleTypes['web']) {
    await bubblesStore.fetchBubbleTypes()
  }
  if (
    !bubblesStore.bubblesByType['web'] ||
    bubblesStore.bubblesByType['web'].length < WEBPAGES_PER_PAGE
  ) {
    try {
      isLoading.value = true
      error.value = null
      await bubblesStore.fetchBubblesByType('web')
    } catch (err) {
      error.value = 'Failed to load web pages. Please try again later.'
      logError('Failed to fetch web pages:', err)
    } finally {
      isLoading.value = false
    }
  }
  getRandomWebPage()
})

async function loadMore() {
  page.value++
  if (page.value * WEBPAGES_PER_PAGE >= webPageBubbles.value.length) {
    try {
      isLoading.value = true
      error.value = null
      await bubblesStore.fetchBubblesByType('web')
    } catch (err) {
      error.value = 'Failed to load more web pages. Please try again later.'
      logError('Failed to fetch web pages:', err)
    } finally {
      isLoading.value = false
    }
  }
}

function getRandomWebPage() {
  if (webPageBubbles.value.length === 0) {
    randomWebPage.value = null
  } else {
    const randomIndex = Math.floor(Math.random() * webPageBubbles.value.length)
    randomWebPage.value = webPageBubbles.value[randomIndex] as WebPageBubble
  }
}

function navigateToAddWebPage() {
  router.push('/dashboard/add')
}

function getCoverPhotoUrl(webpage: WebPageBubble): string | undefined {
  if (webpage.offline && webpage.offlineMediaResources?.cover_photo) {
    return `${dataStore}${webpage.offlineMediaResources.cover_photo}`
  }
  return webpage.coverPhoto || undefined
}

function visitPage(url: string | undefined) {
  if (url) {
    window.open(url, '_blank')
  }
}

function getDomain(url: string | undefined): string {
  if (!url) return 'Unknown Domain'
  try {
    const urlObj = new URL(url)
    return urlObj.hostname.replace('www.', '')
  } catch {
    return 'Invalid URL'
  }
}

watch(filteredWebPages, (webpages) => {
  navigationStore.setCurrentType('web')
  navigationStore.setCurrentTypeItems(webpages.map((webpage) => webpage.uuid))
})

const handleShareSet = async () => {
  isSharingSet.value = true
  try {
    const shareData = {
      shareType: 'set' as const,
      targetBubbleType: 'web',
      isPublic: true,
    }
    const result = await userShareStore.createShareLink(shareData)

    if (result && result.url) {
      toast({
        title: 'Share Link Created',
        description: 'Your set of web pages can be shared via this link.',
        duration: 9000,
        action: h(CopyButton, { textToCopy: result.url, variant: 'secondary' }),
      })
      if (isSupported.value) {
        await copy(result.url)
        if (copied.value) {
          toast({
            title: 'Link Copied!',
            description: 'The share link has been copied to your clipboard.',
            duration: 3000,
          })
        } else {
          toast({
            title: 'Copy Failed',
            description:
              'Could not automatically copy the link. Please use the button in the previous message.',
            variant: 'destructive',
            duration: 5000,
          })
        }
      }
    } else {
      if (!result) {
        toast({
          title: 'Error',
          description: 'Could not create share link for set.',
          variant: 'destructive',
        })
      }
    }
  } catch (error) {
    logError('Error creating web page set share link:', error)
    toast({
      title: 'Error',
      description: 'An unexpected error occurred while sharing the set.',
      variant: 'destructive',
    })
  } finally {
    isSharingSet.value = false
  }
}
</script>
